import { getGymExportSlug } from '../../utils/gymExportBrand';
import { getExportFileDate, getExportTimestamp } from '../../utils/professionalExport';
import {
  buildThemedPrintCss,
  buildZReportPrintExtras,
  buildThemedExcelCss,
  wrapExcelHtmlDocument,
  excelEscape,
  excelNumber,
  excelTd,
} from '../../utils/themeExport';
import { downloadPairedZReportPdf } from './zReportPdfExport';
import {
  Z1_BANK_ACCOUNTS,
  Z1_SALE_ITEMS,
  Z1_PAYMENT_ITEMS,
  Z1_ACTIVE_ITEMS,
  computeZ1Closings,
  sumTodaySale,
  sumTodaySaleCount,
  sumMonthSale,
  sumTodayPayment,
  sumTodayPaymentCount,
  sumMonthPayment,
  sumExpenseGym,
  sumExpensePearl,
} from './zReport1Data';

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function money(value) {
  const n = Number(value) || 0;
  return n.toLocaleString('en-US', { maximumFractionDigits: 0 });
}

function formatReportDate(isoDay) {
  const day = String(isoDay || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return day || '—';
  const d = new Date(`${day}T12:00:00`);
  if (Number.isNaN(d.getTime())) return day;
  return `${d.getDate()}-${d.getMonth() + 1}-${d.getFullYear()}`;
}

function displayManual(value) {
  if (value === '' || value == null) return '0';
  return money(value);
}

function padExcelRow(cells) {
  const row = [...cells];
  while (row.length < 9) row.push('');
  return row.slice(0, 9);
}

function cashLabelForBankRow(bankLabel) {
  if (bankLabel === 'Bank Sale') return 'Today Cash Sale';
  if (bankLabel === 'Bank Received') return 'Cash Received';
  return bankLabel;
}

function toCsvRow(cells) {
  return (cells || []).map((cell) => {
    const s = String(cell ?? '');
    if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  }).join(',');
}

/** Same paired-table shape as classic Z Report PDF. */
function buildZReport1PdfTables(payload) {
  const {
    gymTitle = {},
    reportDate,
    manual = {},
    autoTodayCashSale = 0,
  } = payload;

  const closings = computeZ1Closings(manual, autoTodayCashSale);
  const expense = manual.totalExpenseSummary || {};
  const gymTotal = sumExpenseGym(manual);
  const pearlTotal = sumExpensePearl(manual);

  const bankRows = [
    ['Opening Balance', ...Z1_BANK_ACCOUNTS.map((acc) => displayManual(manual.bankAccounts?.[acc]?.opening))],
    ['Bank Received', ...Z1_BANK_ACCOUNTS.map((acc) => displayManual(manual.bankAccounts?.[acc]?.received))],
    ['Bank Sale', ...Z1_BANK_ACCOUNTS.map((acc) => displayManual(manual.bankAccounts?.[acc]?.sale))],
    ['Pearl Group Expenses', ...Z1_BANK_ACCOUNTS.map((acc) => displayManual(manual.bankAccounts?.[acc]?.pearlGroupExpenses))],
    ['Gym Expenses', ...Z1_BANK_ACCOUNTS.map((acc) => displayManual(manual.bankAccounts?.[acc]?.gymExpenses))],
    ['Closing Balance', ...Z1_BANK_ACCOUNTS.map((acc) => money(closings.closingBanks[acc])), true],
  ];

  const cashRows = [
    ['Opening Balance', displayManual(manual.cashSummary?.opening)],
    ['Cash Received', displayManual(manual.cashSummary?.received)],
    ['Today Cash Sale', money(closings.cashSaleUsed)],
    ['Pearl Group Expenses', displayManual(manual.cashSummary?.pearlGroupExpenses)],
    ['Gym Expenses', displayManual(manual.cashSummary?.gymExpenses)],
    ['Closing Balance', money(closings.closingCash), true],
  ];

  const saleSummary = Z1_SALE_ITEMS.map((row) => ([
    row.label,
    manual.todaySaleSummary?.[row.id]?.count ?? 0,
    displayManual(manual.todaySaleSummary?.[row.id]?.amount),
  ]));
  saleSummary.push([
    'Total Sale',
    sumTodaySaleCount(manual),
    money(sumTodaySale(manual)),
    true,
  ]);

  const saleSummaryManual = Z1_SALE_ITEMS.map((row) => ([
    row.label,
    displayManual(manual.monthSaleSummary?.[row.id]),
  ]));
  saleSummaryManual.push(['Total Sale', money(sumMonthSale(manual)), true]);

  const paymentMethods = Z1_PAYMENT_ITEMS.map((row) => ([
    row.label,
    manual.todayPaymentMethod?.[row.id]?.count ?? 0,
    displayManual(manual.todayPaymentMethod?.[row.id]?.amount),
  ]));
  paymentMethods.push([
    'Total Received',
    sumTodayPaymentCount(manual),
    money(sumTodayPayment(manual)),
    true,
  ]);

  const saleMethods = Z1_PAYMENT_ITEMS.map((row) => ([
    row.label,
    displayManual(manual.monthPaymentMethod?.[row.id]),
  ]));
  saleMethods.push(['Total Received', money(sumMonthPayment(manual)), true]);

  const activeMembers = Z1_ACTIVE_ITEMS.map((row) => ([
    row.label,
    displayManual(manual.totalActiveMembers?.[row.id]),
  ]));

  const expenseSummary = [
    ['Gym Cash Expense', displayManual(expense.gym_cash)],
    ['Gym Bank Expense', displayManual(expense.gym_bank)],
    ['Total', money(gymTotal), true],
    ['Pearl Cash Expense', displayManual(expense.pearl_cash)],
    ['Pearl Bank Expense', displayManual(expense.pearl_bank)],
    ['Total', money(pearlTotal), true],
  ];

  return {
    brandName: gymTitle.name || 'Z REPORT 1',
    branch: gymTitle.branch || '',
    dayLabel: formatReportDate(reportDate),
    bankAccounts: Z1_BANK_ACCOUNTS,
    bankRows,
    cashRows,
    saleSummary,
    saleSummaryManual,
    paymentMethods,
    saleMethods,
    activeMembers,
    expenseSummary,
  };
}

function buildExportData(payload) {
  const {
    gymTitle = {},
    reportDate,
    manual = {},
    autoTodayCashSale = 0,
  } = payload;

  const closings = computeZ1Closings(manual, autoTodayCashSale);
  const expense = manual.totalExpenseSummary || {};
  const gymTotal = sumExpenseGym(manual);
  const pearlTotal = sumExpensePearl(manual);
  const rows = [];

  rows.push(padExcelRow(['Bank Summery', '', '', '', '', '', 'Cash Summery', '', '']));
  rows.push(padExcelRow(['Item', '', ...Z1_BANK_ACCOUNTS, '', 'Item', '', 'Amount']));

  [
    ['Opening Balance', manual.cashSummary?.opening, 'opening'],
    ['Bank Received', manual.cashSummary?.received, 'received'],
    ['Bank Sale', closings.cashSaleUsed, 'sale'],
    ['Pearl Group Expenses', manual.cashSummary?.pearlGroupExpenses, 'pearlGroupExpenses'],
    ['Gym Expenses', manual.cashSummary?.gymExpenses, 'gymExpenses'],
  ].forEach(([bankLabel, cashVal, bankField]) => {
    const left = [
      bankLabel,
      '',
      ...Z1_BANK_ACCOUNTS.map((acc) => displayManual(manual.bankAccounts?.[acc]?.[bankField])),
    ];
    const cashDisplay = bankField === 'sale' ? money(closings.cashSaleUsed) : displayManual(cashVal);
    rows.push(padExcelRow([...left, '', cashLabelForBankRow(bankLabel), '', cashDisplay]));
  });

  rows.push(padExcelRow([
    'Closing Balance',
    '',
    ...Z1_BANK_ACCOUNTS.map((acc) => money(closings.closingBanks[acc])),
    '',
    'Closing Balance',
    '',
    money(closings.closingCash),
  ]));
  rows.push(padExcelRow(['', '', '', '', '', '', '', '', '']));

  rows.push(padExcelRow(['Today Sale Summery', '', '', '', '', '', 'During Month Sale Summery', '', '']));
  rows.push(padExcelRow(['Item', 'No', 'Amount', '', '', '', 'Item', '', 'Amount']));
  Z1_SALE_ITEMS.forEach((row) => {
    rows.push(padExcelRow([
      row.label,
      manual.todaySaleSummary?.[row.id]?.count ?? '0',
      displayManual(manual.todaySaleSummary?.[row.id]?.amount),
      '', '', '',
      row.label, '',
      displayManual(manual.monthSaleSummary?.[row.id]),
    ]));
  });
  rows.push(padExcelRow([
    'Total Sale',
    String(sumTodaySaleCount(manual)),
    money(sumTodaySale(manual)),
    '', '', '',
    'Total Sale', '',
    money(sumMonthSale(manual)),
  ]));
  rows.push(padExcelRow(['', '', '', '', '', '', '', '', '']));

  rows.push(padExcelRow(['Today Payment Method', '', '', '', '', '', 'During Month Payment Method', '', '']));
  rows.push(padExcelRow(['Item', 'No', 'Amount', '', '', '', 'Item', '', 'Amount']));
  Z1_PAYMENT_ITEMS.forEach((row) => {
    rows.push(padExcelRow([
      row.label,
      manual.todayPaymentMethod?.[row.id]?.count ?? '0',
      displayManual(manual.todayPaymentMethod?.[row.id]?.amount),
      '', '', '',
      row.label, '',
      displayManual(manual.monthPaymentMethod?.[row.id]),
    ]));
  });
  rows.push(padExcelRow([
    'Total Received',
    String(sumTodayPaymentCount(manual)),
    money(sumTodayPayment(manual)),
    '', '', '',
    'Total Received', '',
    money(sumMonthPayment(manual)),
  ]));
  rows.push(padExcelRow(['', '', '', '', '', '', '', '', '']));

  rows.push(padExcelRow(['', '', '', '', '', '', 'Total Expense Summery', '', '']));
  rows.push(padExcelRow(['Total Active Members', '', '', '', '', '', 'Item', '', 'Amount']));
  rows.push(padExcelRow(['Item', '', 'Amount', '', '', '', 'Gym Cash Expense', '', displayManual(expense.gym_cash)]));
  rows.push(padExcelRow([
    Z1_ACTIVE_ITEMS[0].label, '', displayManual(manual.totalActiveMembers?.membership_sale_month),
    '', '', '', 'Gym Bank Expense', '', displayManual(expense.gym_bank),
  ]));
  rows.push(padExcelRow([
    Z1_ACTIVE_ITEMS[1].label, '', displayManual(manual.totalActiveMembers?.pt_sale_month),
    '', '', '', 'Total', '', money(gymTotal),
  ]));
  rows.push(padExcelRow([
    Z1_ACTIVE_ITEMS[2].label, '', displayManual(manual.totalActiveMembers?.active_women),
    '', '', '', 'Pearl Cash Expense', '', displayManual(expense.pearl_cash),
  ]));
  rows.push(padExcelRow([
    Z1_ACTIVE_ITEMS[3].label, '', displayManual(manual.totalActiveMembers?.active_men),
    '', '', '', 'Pearl Bank Expense', '', displayManual(expense.pearl_bank),
  ]));
  rows.push(padExcelRow(['', '', '', '', '', '', 'Total', '', money(pearlTotal)]));

  return {
    brandName: gymTitle.name || 'Z REPORT 1',
    branch: gymTitle.branch || '',
    dayLabel: formatReportDate(reportDate),
    rows,
  };
}

function renderGridRows(dataRows) {
  return dataRows.map((row) => {
    const cells = row.map((value, i) => {
      const isGapCol = i === 5;
      if (isGapCol) return '<td class="dz1-gap"></td>';
      const isNum = (i === 1 || i === 2 || i === 4 || i === 7 || i === 8) && value !== '';
      const numOk = isNum && !Number.isNaN(Number(String(value).replace(/,/g, '')));
      return `<td class="${numOk ? 'num' : ''}">${escapeHtml(value)}</td>`;
    }).join('');
    return `<tr>${cells}</tr>`;
  }).join('');
}

function buildPrintHtml(payload) {
  const data = buildExportData(payload);
  const printCss = buildThemedPrintCss(buildZReportPrintExtras(), { compact: true, fitOnePage: true });

  return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Z Report 1 — ${escapeHtml(data.dayLabel)}</title><style>${printCss}</style></head>
<body class="fit-one-page z-report-print">
  <table class="zr-banner">
    <tr><td class="zr-brand">${escapeHtml(data.brandName)}</td></tr>
    ${data.branch ? `<tr><td class="zr-branch">${escapeHtml(data.branch)}</td></tr>` : ''}
    <tr><td>
      <div class="zr-meta">
        <span>Z-REPORT</span>
        <span>DATE : ${escapeHtml(data.dayLabel)}</span>
      </div>
    </td></tr>
  </table>
  <p style="text-align:center;font-size:9px;color:#666;margin:0 0 6px;">Generated: ${escapeHtml(getExportTimestamp())}</p>
  <table style="width:100%;border-collapse:collapse;font-size:10px;">${renderGridRows(data.rows)}</table>
</body></html>`;
}

function buildCsv(payload) {
  const data = buildExportData(payload);
  const lines = [
    data.brandName,
    data.branch,
    `Z-REPORT — ${data.dayLabel}`,
    `Generated: ${getExportTimestamp()}`,
    '',
    toCsvRow(['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I']),
    ...data.rows.map((row) => toCsvRow(row)),
  ];
  return lines.join('\n');
}

function buildExcelHtml(payload) {
  const data = buildExportData(payload);
  const body = `<table class="sheet">
    <tr><td colspan="9" style="font-weight:700;text-align:center;">${excelEscape(data.brandName)}</td></tr>
    ${data.branch ? `<tr><td colspan="9" style="text-align:center;">${excelEscape(data.branch)}</td></tr>` : ''}
    <tr><td colspan="9" style="font-weight:700;text-align:center;">Z-REPORT — DATE : ${excelEscape(data.dayLabel)}</td></tr>
    ${data.rows.map((row) => `<tr>${row.map((cell, i) => {
      if (i === 5) return excelTd('');
      const num = (i === 1 || i === 2 || i === 4 || i === 7 || i === 8) && cell !== '';
      return excelTd(num ? excelNumber(cell) : excelEscape(cell), num);
    }).join('')}</tr>`).join('')}
  </table>`;

  return wrapExcelHtmlDocument({
    title: `Z Report 1 — ${data.dayLabel}`,
    sheetName: 'Z Report 1',
    bodyHtml: body,
    css: buildThemedExcelCss(),
    fitWidth: 1,
    fitHeight: 0,
  });
}

function fileBase(payload) {
  const slug = getGymExportSlug() || 'gym';
  const day = String(payload.reportDate || getExportFileDate()).slice(0, 10);
  return `z-report-1-${slug}-${day}`;
}

function downloadBlob(content, filename, type) {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.setTimeout(() => URL.revokeObjectURL(url), 200);
}

function openHtmlDocument(html, { autoPrint = false } = {}) {
  let url = '';
  try {
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    url = URL.createObjectURL(blob);
  } catch (err) {
    console.error('Z Report 1 HTML blob failed', err);
    return false;
  }

  if (autoPrint) {
    const iframe = document.createElement('iframe');
    iframe.setAttribute('title', 'Z Report 1 Print');
    iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;opacity:0;pointer-events:none;';
    document.body.appendChild(iframe);
    iframe.onload = () => {
      try {
        iframe.contentWindow?.focus();
        iframe.contentWindow?.print();
      } catch { /* ignore */ }
      window.setTimeout(() => {
        if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
        try { URL.revokeObjectURL(url); } catch { /* ignore */ }
      }, 60_000);
    };
    iframe.src = url;
    return true;
  }

  const win = window.open(url, '_blank');
  if (!win || win === window) {
    try {
      const link = document.createElement('a');
      link.href = url;
      link.download = 'z-report-1-preview.html';
      link.click();
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
      return 'downloaded';
    } catch {
      try { URL.revokeObjectURL(url); } catch { /* ignore */ }
      return false;
    }
  }
  try { win.focus(); } catch { /* ignore */ }
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
  return true;
}

export function printDailyZReport1(payload) {
  try {
    return openHtmlDocument(buildPrintHtml(payload), { autoPrint: true });
  } catch (err) {
    console.error('Z Report 1 print failed', err);
    return false;
  }
}

export function exportDailyZReport1Csv(payload) {
  downloadBlob(buildCsv(payload), `${fileBase(payload)}.csv`, 'text/csv;charset=utf-8');
}

export function exportDailyZReport1Excel(payload) {
  downloadBlob(buildExcelHtml(payload), `${fileBase(payload)}.xls`, 'application/vnd.ms-excel;charset=utf-8');
}

/** Download a real .pdf file (same design as classic Z Report). */
export function exportDailyZReport1Pdf(payload) {
  try {
    return downloadPairedZReportPdf(buildZReport1PdfTables(payload), `${fileBase(payload)}.pdf`);
  } catch (err) {
    console.error('Z Report 1 PDF export failed', err);
    return false;
  }
}
